/**
 * AuditTrail — unified audit log viewer.
 *
 * Two sources share `db.audit_logs`:
 *   • Legacy application-workflow logs (COP checks, contracts, bank
 *     details…) written inline by server.py — no `module` field.
 *   • Automatic middleware logs — EVERY write action on the enabled
 *     modules (collections & legal by default): who, when, what, which
 *     case. Configurable via the "Audit settings" checkbox dropdown.
 */
import { useState, useEffect, useCallback } from "react";
import {
  ScrollText, Loader2, Search, RefreshCw, Settings2, Landmark,
  Banknote, FileText, Building2, Globe, Shield,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { getAuditLogs, getAuditSettings, updateAuditSettings } from "@/lib/api";
import { auditStatusBadge } from "@/lib/auditStatus";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const MODULE_META = {
  collections:  { label: "Collections",       icon: Banknote,  tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  legal:        { label: "Legal",             icon: Landmark,  tone: "bg-red-50 text-red-700 border-red-200" },
  open_banking: { label: "Open Banking",      icon: Globe,     tone: "bg-sky-50 text-sky-700 border-sky-200" },
  applications: { label: "Applications",      icon: FileText,  tone: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  aus:          { label: "AUS",               icon: Building2, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  admin:        { label: "Admin",             icon: Shield,    tone: "bg-slate-100 text-slate-700 border-slate-300" },
};

export default function AuditTrail() {
  const [logs, setLogs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState("all");
  const [q, setQ]             = useState("");
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { limit: 300 };
      if (moduleFilter !== "all") filters.module = moduleFilter;
      if (q.trim()) filters.q = q.trim();
      const data = await getAuditLogs(filters);
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Failed to load audit logs: " + errorDetailMessage(e, e.message));
    } finally {
      setLoading(false);
    }
  }, [moduleFilter, q]);

  useEffect(() => { load(); }, [moduleFilter]);          // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    getAuditSettings().then(setSettings).catch(() => {});
  }, []);

  const toggleModule = async (key, checked) => {
    if (!settings) return;
    const next = checked
      ? [...new Set([...settings.enabled_modules, key])]
      : settings.enabled_modules.filter(k => k !== key);
    setSavingSettings(true);
    try {
      await updateAuditSettings(next);
      setSettings(s => ({ ...s, enabled_modules: next }));
      toast.success("Audit settings saved");
    } catch (e) {
      toast.error("Failed to save: " + errorDetailMessage(e, e.message));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ScrollText className="w-6 h-6 text-slate-700" />
            Audit Trail
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Every action across the enabled modules — who did what, when, and on which case.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8" onClick={load} disabled={loading} data-testid="audit-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {/* Which modules to audit — checkbox dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8" data-testid="audit-settings-btn">
                <Settings2 className="w-3.5 h-3.5 mr-1.5" />
                Audit settings
                {savingSettings && <Loader2 className="w-3 h-3 ml-1.5 animate-spin" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">Modules being audited</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {(settings?.available_modules || []).map(m => (
                <DropdownMenuCheckboxItem
                  key={m.key}
                  checked={settings?.enabled_modules?.includes(m.key)}
                  onCheckedChange={(v) => toggleModule(m.key, v)}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`audit-module-toggle-${m.key}`}
                >
                  {m.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <p className="px-2 py-1.5 text-[10px] text-muted-foreground leading-snug">
                Every create / update / delete on a ticked module is logged automatically —
                event, user, timestamp and case reference.
              </p>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="h-8 w-[210px] text-xs" data-testid="audit-module-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modules</SelectItem>
            {Object.entries(MODULE_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.label}</SelectItem>
            ))}
            <SelectItem value="applications_legacy">Applications (legacy workflow logs)</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 flex-1 max-w-md">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search event, case ref, user…"
            className="h-8 text-xs"
            data-testid="audit-search"
          />
          <Button size="sm" variant="outline" className="h-8" onClick={load} data-testid="audit-search-btn">
            <Search className="w-3.5 h-3.5" />
          </Button>
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{logs.length} entries</span>
      </div>

      {/* Log table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : logs.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No audit entries match. Actions appear here the moment they happen on an audited module.
            </div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-4 py-2 font-semibold whitespace-nowrap">Timestamp</th>
                    <th className="text-left px-4 py-2 font-semibold">Module</th>
                    <th className="text-left px-4 py-2 font-semibold">Event</th>
                    <th className="text-left px-4 py-2 font-semibold">Case / App</th>
                    <th className="text-left px-4 py-2 font-semibold">User</th>
                    <th className="text-left px-4 py-2 font-semibold">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y" data-testid="audit-log-table">
                  {logs.map((log, i) => <LogRow key={log.id || i} log={log} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function LogRow({ log }) {
  const meta = MODULE_META[log.module];
  const ts = log.created_at ? new Date(log.created_at) : null;
  const badge = auditStatusBadge(log);
  return (
    <tr className="hover:bg-slate-50 align-top">
      <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-600 tabular-nums">
        {ts ? ts.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
      </td>
      <td className="px-4 py-2">
        {meta ? (
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${meta.tone}`}>
            <meta.icon className="w-3 h-3" /> {meta.label}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium bg-indigo-50 text-indigo-700 border-indigo-200">
            <FileText className="w-3 h-3" /> Applications
          </span>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="text-xs font-medium text-slate-800" title={log.path || ""}>
          {prettyAction(log.action)}
        </div>
        {badge && (
          <Badge variant="outline" className="text-[9px] mt-0.5 text-red-700 border-red-300 bg-red-50" data-testid="audit-status-badge">
            {badge}
          </Badge>
        )}
      </td>
      <td className="px-4 py-2 text-xs font-mono text-slate-600">
        {log.case_ref || log.application_id || "—"}
      </td>
      <td className="px-4 py-2 text-xs">{log.performed_by || "—"}</td>
      <td className="px-4 py-2 text-xs text-muted-foreground max-w-[280px]">
        <span className="line-clamp-2" title={typeof log.details === "string" ? log.details : ""}>
          {typeof log.details === "string" ? log.details : log.details ? JSON.stringify(log.details) : "—"}
        </span>
      </td>
    </tr>
  );
}

// Legacy actions are snake_case ("cop_check_completed"); middleware ones
// are already readable ("collections › ageing › notes — created/actioned").
function prettyAction(action) {
  if (!action) return "—";
  return action.includes("›") || action.includes(" ") ? action : action.replace(/_/g, " ");
}
